/**
 * AgentDetailPage — View/edit a single agent's configuration and activity.
 * Task 4.9
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Bot,
  Pencil,
  Trash2,
  ArrowLeft,
  Cpu,
  Shield,
  Activity,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { EmptyState } from "@/components/ui/EmptyState";
import { useAgentStore } from "@/stores/useAgentStore";
import { useSandboxStore } from "@/stores/useSandboxStore";
import type { Agent } from "@/types/agent";

const modelOptions = [
  "claude-sonnet-4-20250514",
  "claude-opus-4-20250514",
  "gpt-4o",
  "gpt-4o-mini",
  "gemini-2.0-flash",
];

export function AgentDetailPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { agents, loadAgents, updateAgent, deleteAgent, isLoading } =
    useAgentStore();
  const { sandboxes } = useSandboxStore();

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Agent>>({});

  useEffect(() => {
    if (agents.length === 0) {
      loadAgents();
    }
  }, [agents.length, loadAgents]);

  const agent = agents.find((a) => a.id === agentId);

  useEffect(() => {
    if (agent) {
      setEditForm({
        name: agent.name,
        model: agent.model,
        systemPrompt: agent.systemPrompt,
        permissions: { ...agent.permissions },
      });
    }
  }, [agent]);

  const handleSave = useCallback(async () => {
    if (!agentId) return;
    await updateAgent(agentId, editForm);
    setIsEditing(false);
  }, [agentId, editForm, updateAgent]);

  const handleDelete = useCallback(async () => {
    if (!agentId) return;
    await deleteAgent(agentId);
    navigate("/dashboard/agents");
  }, [agentId, deleteAgent, navigate]);

  if (!agent && !isLoading) {
    return (
      <div className="p-6">
        <EmptyState
          icon={<Bot className="h-6 w-6" />}
          title="Agent not found"
          description="The agent you're looking for doesn't exist or has been deleted."
          action={
            <Button variant="secondary" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col" data-testid="agent-detail-page">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{agent.name}</h1>
              <Badge variant="primary">{agent.model}</Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Created {new Date(agent.createdAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} loading={isLoading}>
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Configuration */}
          <section className="rounded-xl border border-white/10 bg-surface-raised p-5">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
              <Cpu className="h-5 w-5 text-primary" />
              Configuration
            </h2>

            <div className="space-y-4">
              {isEditing ? (
                <>
                  <Input
                    label="Name"
                    value={editForm.name ?? ""}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                  <Select
                    label="Model"
                    value={editForm.model ?? ""}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, model: e.target.value }))
                    }
                  >
                    {modelOptions.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Select>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-foreground">
                      System Prompt
                    </label>
                    <textarea
                      data-testid="edit-system-prompt"
                      value={editForm.systemPrompt ?? ""}
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          systemPrompt: e.target.value,
                        }))
                      }
                      rows={6}
                      className="w-full resize-none rounded-md border border-white/10 bg-surface px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <span className="text-xs font-medium uppercase text-muted">
                      Model
                    </span>
                    <p className="mt-1 text-sm text-foreground">{agent.model}</p>
                  </div>
                  <div>
                    <span className="text-xs font-medium uppercase text-muted">
                      System Prompt
                    </span>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                      {agent.systemPrompt || "No system prompt configured"}
                    </p>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* Permissions */}
          <section className="rounded-xl border border-white/10 bg-surface-raised p-5">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
              <Shield className="h-5 w-5 text-accent-lavender" />
              Permissions
            </h2>
            <div className="space-y-2">
              {Object.entries(agent.permissions).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No permissions configured
                </p>
              ) : (
                Object.entries(agent.permissions).map(([perm, value]) => (
                  <div
                    key={perm}
                    className="flex items-center justify-between rounded-lg border border-white/5 bg-surface px-3 py-2"
                  >
                    <span className="text-sm text-foreground">{perm}</span>
                    <Badge variant={value === "allow" ? "success" : "danger"}>
                      {value}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* Active Sessions */}
          <section className="rounded-xl border border-white/10 bg-surface-raised p-5 lg:col-span-2">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-white">
              <Activity className="h-5 w-5 text-accent-sage" />
              Active Sessions
            </h2>
            {sandboxes.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No active sessions for this agent
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {sandboxes.slice(0, 6).map((sb) => (
                  <div
                    key={sb.id}
                    className="rounded-lg border border-white/5 bg-surface px-4 py-3"
                  >
                    <p className="truncate text-sm font-medium text-foreground">
                      {sb.endpointUrl ?? sb.id}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <div
                        className={`h-2 w-2 rounded-full ${
                          sb.state === "active"
                            ? "bg-emerald-400"
                            : sb.state === "error"
                              ? "bg-red-400"
                              : "bg-amber-400"
                        }`}
                      />
                      <span className="text-xs text-muted">{sb.state}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="mx-4 w-full max-w-md rounded-xl border border-white/10 bg-surface-raised p-6"
            data-testid="delete-confirm"
          >
            <h3 className="text-lg font-semibold text-white">Delete Agent</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Are you sure you want to delete &quot;{agent.name}&quot;? This
              action cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDelete}
                loading={isLoading}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
